/**
 * Risk & Intelligence OS — API v2 (Decision Object, Audit, Events)
 * Base URL from apiConfig; endpoints: /api/v2/*
 */
import { getApiBase } from './apiConfig';

export interface SubmitToARINv2Response {
  success: boolean;
  arin_request_id?: string;
  decision_id?: string;
  decision_object_id?: string;
  error?: string;
}

export interface DefiExposureSnapshot {
  tvl_change_7d?: number;
  protocol_risks?: string[];
}

export interface AnalysisSnapshot {
  defi_exposure?: DefiExposureSnapshot;
  on_chain_metrics?: { whale_movement?: string; tvl?: number };
  rating?: { overall?: number; grade?: string };
  [key: string]: unknown;
}

export interface DecisionObjectStored {
  decision_id?: string;
  object_id: string;
  source_module: string;
  object_type: string;
  analysis_snapshot?: AnalysisSnapshot;
  timestamp?: string;
  created_at?: string;
  arin_request_id?: string;
}

export interface DecisionsResponse {
  success: boolean;
  decision_objects?: DecisionObjectStored[];
  error?: string;
}

export interface AuditRecord {
  id: string;
  category: string;
  decision_id?: string;
  symbol?: string;
  payload?: Record<string, unknown>;
  input_hash?: string;
  timestamp: string;
  source: string;
}

export interface AuditTraceResponse {
  success: boolean;
  trace?: AuditRecord[];
  decision_id?: string;
  error?: string;
}

const api = () => getApiBase();

/** POST /api/v2/analysis/submit-to-arin */
export async function submitToARINv2(symbol: string): Promise<SubmitToARINv2Response> {
  const base = api();
  const url = base ? `${base}/api/v2/analysis/submit-to-arin` : '/api/v2/analysis/submit-to-arin';
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ symbol: symbol.toLowerCase() }),
  });
  const data = await res.json();
  if (!res.ok) {
    return { success: false, error: data.error || `HTTP ${res.status}` };
  }
  return data;
}

/** GET /api/v2/decisions/:symbol */
export async function getDecisions(symbol: string): Promise<DecisionsResponse> {
  const base = api();
  const url = base ? `${base}/api/v2/decisions/${encodeURIComponent(symbol.toLowerCase())}` : `/api/v2/decisions/${encodeURIComponent(symbol.toLowerCase())}`;
  const res = await fetch(url);
  const data = await res.json();
  if (!res.ok) {
    return { success: false, error: data.error || `HTTP ${res.status}` };
  }
  return data;
}

/** GET /api/v2/audit/trace/:decision_id */
export async function getAuditTrace(decisionId: string): Promise<AuditTraceResponse> {
  const base = api();
  const url = base ? `${base}/api/v2/audit/trace/${encodeURIComponent(decisionId)}` : `/api/v2/audit/trace/${encodeURIComponent(decisionId)}`;
  const res = await fetch(url);
  const data = await res.json();
  if (!res.ok) {
    return { success: false, error: data.error || `HTTP ${res.status}` };
  }
  return data;
}

/** POST /api/v2/events/publish (optional) */
export async function publishEvent(eventType: string, payload: Record<string, unknown>): Promise<{ success: boolean; event_id?: string; error?: string }> {
  const base = api();
  const url = base ? `${base}/api/v2/events/publish` : '/api/v2/events/publish';
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event_type: eventType, payload: payload || {} }),
  });
  const data = await res.json();
  if (!res.ok) {
    return { success: false, error: data.error || `HTTP ${res.status}` };
  }
  return data;
}
